import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Chip, Divider, Paper, Stack, Typography,
} from '@mui/material';
import Highcharts from 'highcharts/highcharts-gantt';
import HighchartsReact from 'highcharts-react-official';
import type {
  Irrigation, RiegoGanttProps, SelectedPoint, ZoneIndex,
} from './riego.types';
import IrrigationDetailPanel from './components/IrrigationDetailPanel';
import { STATUS_COLORS } from './dashboardColors';
import {
  LEGEND_ITEMS, NOW_TICK_INTERVAL_MS, RESUMABLE_STATUSES, SCHEDULED_STATUSES, STATUS_LABEL,
} from './riego.constants';
import buildGanttOptions from './riegoGantt.builder';
import {
  accentBarSx,
  headerStackSx,
  legendStackSx,
  legendSwatchSx,
  nowLineSwatchSx,
  paperSx,
  peakChipSx,
  peakHourSwatchSx,
  pumpNameSx,
  zonesChipSx,
} from './riegoGantt.styles';

const RiegoGantt: React.FC<RiegoGanttProps> = ({
  pumpSystem, zones, scheduled, real, rangeStart, rangeEnd, footer, headerAction, peakHour,
  canControl, isSending, onDelete, onStop, onActivate,
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const [selected, setSelected] = useState<SelectedPoint | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const zoneIndex = useMemo<ZoneIndex>(
    () => new Map(zones.map((z, i) => [z.id, i])),
    [zones],
  );

  const hasRunning = useMemo(() => real.some((r) => r.status === 'running'), [real]);
  useEffect(() => {
    if (!hasRunning) return undefined;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), NOW_TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasRunning]);

  const handlePointClick = useCallback(
    (kind: 'scheduled' | 'real', id: number) => setSelected({ kind, id }),
    [],
  );

  const options = useMemo<Highcharts.Options>(
    () => buildGanttOptions({
      zones,
      scheduled,
      real,
      rangeStart,
      rangeEnd,
      zoneIndex,
      peakHour,
      onPointClick: handlePointClick,
      nowMs,
    }),
    [zones, scheduled, real, rangeStart, rangeEnd, zoneIndex, peakHour, handlePointClick, nowMs],
  );

  const selectedIrrigation = useMemo<Irrigation | null>(() => {
    if (!selected) return null;
    const pool = selected.kind === 'scheduled' ? scheduled : real;
    return pool.find((it) => it.id === selected.id) ?? null;
  }, [selected, scheduled, real]);

  const zoneNameById = useMemo(
    () => new Map(zones.map((z) => [z.id, z.name])),
    [zones],
  );

  useEffect(() => {
    if (selected && !scheduled.concat(real).some((it) => it.id === selected.id)) {
      setSelected(null);
    }
  }, [scheduled, real, selected]);

  useEffect(() => {
    if (chartRef.current?.chart) chartRef.current.chart.reflow();
  }, [zones.length, rangeStart, rangeEnd]);

  const handleDelete = () => {
    if (!selectedIrrigation || !onDelete) return;
    onDelete(selectedIrrigation.id, selectedIrrigation.zoneId).then((ok) => {
      if (ok) setSelected(null);
    });
  };

  const handleStop = () => {
    if (!selectedIrrigation || !onStop) return;
    onStop(selectedIrrigation.id, selectedIrrigation.zoneId).then((ok) => {
      if (ok) setSelected(null);
    });
  };

  const handleActivate = () => {
    if (!selectedIrrigation || !onActivate) return;
    onActivate(selectedIrrigation.id).then((ok) => {
      if (ok) setSelected(null);
    });
  };

  return (
    <>
    <Paper elevation={0} sx={paperSx}>
      <Stack direction="row" alignItems="center" mb={1.5} sx={headerStackSx}>
        <Box sx={accentBarSx} />
        <Typography variant="h6" sx={pumpNameSx}>{pumpSystem.name}</Typography>
        <Chip size="small" variant="outlined" label={`Modo: ${pumpSystem.mode || '—'}`} sx={{ fontWeight: 500 }} />
        {peakHour && (
          <Chip
            size="small"
            variant="outlined"
            label={`Hora Punta: ${peakHour.init} – ${peakHour.end}`}
            sx={peakChipSx}
          />
        )}
        <Chip size="small" label={`${zones.length} zonas`} sx={zonesChipSx} />
        {headerAction && <Box sx={{ flexGrow: 1 }} />}
        {headerAction}
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      <Stack direction="row" mb={1.5} alignItems="center" sx={legendStackSx}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>LEYENDA:</Typography>
        {LEGEND_ITEMS.map(({ status, showBorder, swatch }) => (
          <Stack key={status} direction="row" alignItems="center" spacing={0.5}>
            <Box sx={legendSwatchSx(STATUS_COLORS[status], showBorder, swatch)} />
            <Typography variant="caption">{STATUS_LABEL[status]}</Typography>
          </Stack>
        ))}
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={peakHourSwatchSx} />
          <Typography variant="caption">Hora Punta</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={nowLineSwatchSx} />
          <Typography variant="caption">Ahora</Typography>
        </Stack>
      </Stack>

      {zones.length === 0 ? (
        <Box p={2} textAlign="center">
          <Typography color="text.secondary">Sin zonas asociadas a este equipo.</Typography>
        </Box>
      ) : (
        <HighchartsReact
          highcharts={Highcharts}
          constructorType="ganttChart"
          options={options}
          ref={chartRef}
        />
      )}

      {footer}
    </Paper>

    {selectedIrrigation && (
      <IrrigationDetailPanel
        irrigation={selectedIrrigation}
        zoneName={zoneNameById.get(selectedIrrigation.zoneId) || '—'}
        onClose={() => setSelected(null)}
        onDelete={onDelete ? handleDelete : undefined}
        onStop={onStop ? handleStop : undefined}
        onActivate={onActivate ? handleActivate : undefined}
        canDelete={canControl && SCHEDULED_STATUSES.has(selectedIrrigation.status)}
        canStop={canControl && selectedIrrigation.status === 'running'}
        canActivate={canControl && RESUMABLE_STATUSES.has(selectedIrrigation.status)}
        isDeleting={isSending}
        isStopping={isSending}
        isActivating={isSending}
      />
    )}
    </>
  );
};

export default RiegoGantt;
